'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Phone } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'Repairs', href: '/repairs' },
  { label: 'Refurbished Phones', href: '/refurbished-phones' },
  { label: 'About', href: '/about' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'backdrop-blur-sm shadow-lg'
            : 'backdrop-blur-sm'
        }`}
        style={{ 
          backgroundColor: scrolled ? 'rgba(10,10,10,0.97)' : 'rgba(10,10,10,0.92)',
          borderBottom: '1px solid #2a2a2a' 
        }}
      >
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link href="/" className="flex items-center gap-2.5 flex-shrink-0">
              <AppLogo size={36} />
              <span className="font-extrabold text-lg tracking-tight" style={{ color: '#f5f0e8' }}>
                LP Refurbished
              </span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1">
              {navLinks?.map((link) => {
                const isActive = pathname === link?.href;
                return (
                  <Link
                    key={`nav-${link?.href}`}
                    href={link?.href}
                    className={isActive ? 'nav-link-active' : 'nav-link'}
                  >
                    {link?.label}
                  </Link>
                );
              })}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <a
                href="tel:0481446276"
                className="flex flex-col items-end text-right"
              >
                <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#5a4a35' }}>Call Now</span>
                <span className="text-sm font-bold" style={{ color: '#f5f0e8' }}>0481 446 276</span>
              </a>
              <Link
                href="/repairs"
                className="btn-primary text-sm py-2.5 px-6"
                style={{ minWidth: 0 }}
              >
                Book a Repair
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 rounded-lg transition-colors duration-150"
              style={{ color: '#f5f0e8' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1a1a1a')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 modal-backdrop md:hidden animate-fade-in"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 z-50 w-72 md:hidden flex flex-col transition-transform duration-300 ease-out ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ backgroundColor: '#0d0d0d', borderLeft: '1px solid #2a2a2a' }}
      >
        <div className="flex items-center justify-between px-5 h-16 flex-shrink-0" style={{ borderBottom: '1px solid #2a2a2a' }}>
          <div className="flex items-center gap-2.5">
            <AppLogo size={32} />
            <span className="font-extrabold text-base" style={{ color: '#f5f0e8' }}>LP Refurbished</span>
          </div>
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-lg transition-colors"
            style={{ color: '#f5f0e8' }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#1a1a1a')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-4 py-5 flex flex-col gap-1">
          {navLinks?.map((link) => {
            const isActive = pathname === link?.href;
            return (
              <Link
                key={`mobile-nav-${link?.href}`}
                href={link?.href}
                className={`flex items-center px-4 py-3 rounded-xl text-sm font-semibold transition-colors duration-150 ${
                  isActive
                    ? '' : ''
                }`}
                style={{ 
                  color: isActive ? '#d4a843' : '#c9b99a',
                  backgroundColor: isActive ? 'rgba(212,168,67,0.1)' : 'transparent'
                }}
              >
                {link?.label}
              </Link>
            );
          })}
        </nav>

        <div className="px-4 pb-6 pt-4 flex flex-col gap-3" style={{ borderTop: '1px solid #2a2a2a' }}>
          <a
            href="tel:0481446276"
            className="flex items-center gap-2 text-sm font-bold"
            style={{ color: '#d4a843' }}
          >
            <Phone size={16} />
            0481 446 276
          </a>
          <Link href="/repairs" className="btn-primary w-full justify-center text-sm py-3">
            Book a Repair
          </Link>
        </div>
      </div>
    </>
  );
}